import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, hasPermission } from "@/lib/auth";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Search,
  Plus,
  Building2,
  FileText,
  ClipboardCheck,
  UserPlus,
  Loader2,
  X,
  Bell,
} from "lucide-react";
import NewClientDialog from "@/components/NewClientDialog";
import PickClientDialog from "@/components/PickClientDialog";

export default function TopBar() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const containerRef = useRef(null);

  const [newClientOpen, setNewClientOpen] = useState(false);
  const [pickerAction, setPickerAction] = useState(null); // 'new-contract' | 'new-pv' | null
  const [alertsCount, setAlertsCount] = useState(0);
  const [alertsItems, setAlertsItems] = useState([]);
  const [pvAlertsItems, setPvAlertsItems] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const alertsRef = useRef(null);

  // Debounced global search
  useEffect(() => {
    if (!q.trim()) {
      setResults(null);
      return;
    }
    let alive = true;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: q.trim() } });
        if (alive) setResults(data);
      } catch {
        if (alive) setResults(null);
      } finally {
        if (alive) setSearching(false);
      }
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q]);

  // Fetch expiring-contract alerts once per session (refresh every 5 min)
  useEffect(() => {
    let alive = true;
    let toastShown = false;
    const loadAlerts = async () => {
      try {
        const [contracts, pvs] = await Promise.all([
          api.get("/alerts/expiring-contracts"),
          api.get("/alerts/missing-pvs"),
        ]);
        if (!alive) return;
        const cCount = contracts.data.count || 0;
        const pCount = pvs.data.count || 0;
        setAlertsCount(cCount + pCount);
        setAlertsItems(contracts.data.items || []);
        setPvAlertsItems(pvs.data.items || []);
        // First-time toast on login: warn about contracts expiring in ≤ 30 days
        if (!toastShown && cCount > 0) {
          const upcoming = (contracts.data.items || []).filter((a) => a.zile_ramase >= 0 && a.zile_ramase <= 30);
          if (upcoming.length > 0) {
            const { toast } = await import("sonner");
            toast.warning(
              `${upcoming.length} contract${upcoming.length === 1 ? "" : "e"} expiră în următoarele 30 de zile`,
              { duration: 8000, description: "Deschide clopoțelul 🔔 din bara de sus pentru detalii." }
            );
            toastShown = true;
          }
        }
        // Toast for missing/overdue PVs
        if (!toastShown && pCount > 0) {
          const { toast } = await import("sonner");
          toast.info(`${pCount} PV-uri de făcut / restante`, {
            duration: 8000,
            description: "Contractele cu frecvență au PV-uri restante sau urmează.",
          });
          toastShown = true;
        }
      } catch {}
    };
    loadAlerts();
    const iv = setInterval(loadAlerts, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  // Close alerts dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (alertsRef.current && !alertsRef.current.contains(e.target)) {
        setShowAlerts(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const totalHits =
    (results?.clients?.length || 0) +
    (results?.contracts?.length || 0) +
    (results?.pvs?.length || 0) +
    (results?.users?.length || 0);

  const go = (path) => {
    setQ("");
    setResults(null);
    setShowResults(false);
    nav(path);
  };

  return (
    <>
      <header
        className="sticky top-0 z-30 bg-white border-b border-border shadow-sm"
        data-testid="app-topbar"
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          {/* Search */}
          <div ref={containerRef} className="relative flex-1 max-w-2xl">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onFocus={() => setShowResults(true)}
                onChange={(e) => {
                  setQ(e.target.value);
                  setShowResults(true);
                }}
                placeholder="Caută clienți, contracte, PV-uri, useri..."
                className="pl-9 pr-9 h-10 bg-slate-50 border-slate-200 text-foreground placeholder:text-muted-foreground focus-visible:ring-gold/40 focus-visible:border-gold"
                data-testid="topbar-search-input"
              />
              {q && (
                <button
                  onClick={() => {
                    setQ("");
                    setResults(null);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-200 text-muted-foreground"
                  data-testid="topbar-search-clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Results dropdown */}
            {showResults && q.trim() && (
              <div
                className="absolute left-0 right-0 mt-2 bg-white border border-border rounded-md shadow-2xl overflow-hidden max-h-[70vh] overflow-y-auto"
                data-testid="topbar-search-results"
              >
                {searching ? (
                  <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Se caută...
                  </div>
                ) : totalHits === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    Niciun rezultat pentru „{q}"
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {results?.clients?.length > 0 && (
                      <SearchGroup label="Clienți" icon={Building2}>
                        {results.clients.map((c) => (
                          <SearchItem
                            key={c.id}
                            testid={`search-client-${c.id}`}
                            onClick={() => go(`/clienti/${c.id}`)}
                            title={c.denumire}
                            subtitle={`CUI ${c.cui}${c.localitate ? ` · ${c.localitate}` : ""}`}
                          />
                        ))}
                      </SearchGroup>
                    )}
                    {results?.contracts?.length > 0 && (
                      <SearchGroup label="Contracte" icon={FileText}>
                        {results.contracts.map((c) => (
                          <SearchItem
                            key={c.id}
                            testid={`search-contract-${c.id}`}
                            onClick={() => go(`/clienti/${c.client_id}`)}
                            title={`Contract ${c.numar}`}
                            subtitle={`${c.client_denumire || "—"}${c.data ? ` · ${c.data}` : ""}`}
                          />
                        ))}
                      </SearchGroup>
                    )}
                    {results?.pvs?.length > 0 && (
                      <SearchGroup label="Procese Verbale" icon={ClipboardCheck}>
                        {results.pvs.map((p) => (
                          <SearchItem
                            key={p.id}
                            testid={`search-pv-${p.id}`}
                            onClick={() => go(`/clienti/${p.client_id}`)}
                            title={`PV ${p.numar}`}
                            subtitle={`${p.client_denumire || "—"}${p.data ? ` · ${p.data}` : ""}`}
                          />
                        ))}
                      </SearchGroup>
                    )}
                    {results?.users?.length > 0 && (
                      <SearchGroup label="Utilizatori" icon={UserPlus}>
                        {results.users.map((u) => (
                          <SearchItem
                            key={u.id}
                            testid={`search-user-${u.id}`}
                            onClick={() => go(`/utilizatori`)}
                            title={u.name}
                            subtitle={`${u.email} · ${u.role === "admin" ? "Administrator" : "Utilizator"}`}
                          />
                        ))}
                      </SearchGroup>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Bell icon with expiration alerts count */}
          <div ref={alertsRef} className="relative">
            <button
              onClick={() => setShowAlerts((v) => !v)}
              className={`relative h-10 w-10 rounded-md border flex items-center justify-center transition-all ${
                alertsCount > 0
                  ? "border-red-500 bg-red-50 hover:bg-red-100 animate-pulse"
                  : "border-border bg-white hover:bg-slate-50"
              }`}
              data-testid="topbar-bell-btn"
              aria-label="Alerte"
            >
              <Bell className={`w-5 h-5 ${alertsCount > 0 ? "text-red-600" : "text-slate-700"}`} />
              {alertsCount > 0 && (
                <>
                  <span
                    className="absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-600 text-white text-xs font-black flex items-center justify-center border-2 border-white shadow-lg"
                    data-testid="topbar-bell-count"
                  >
                    {alertsCount > 99 ? "99+" : alertsCount}
                  </span>
                  <span className="absolute -top-2 -right-2 w-[22px] h-[22px] rounded-full bg-red-500 opacity-60 animate-ping" />
                </>
              )}
            </button>
            {showAlerts && (
              <div
                className="absolute right-0 mt-2 w-80 bg-white border border-border rounded-md shadow-2xl overflow-hidden z-40"
                data-testid="topbar-alerts-dropdown"
              >
                <div className="px-4 py-3 border-b border-border bg-red-50">
                  <p className="label-eyebrow text-red-700">Alerte</p>
                  <p className="text-sm font-semibold text-red-900">
                    {alertsCount === 0 ? "Nicio alertă" : `${alertsCount} de urmărit`}
                  </p>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {alertsItems.length === 0 && pvAlertsItems.length === 0 ? (
                    <p className="text-center text-sm text-muted-foreground py-6">Toate contractele sunt la zi.</p>
                  ) : (
                    <>
                      {alertsItems.length > 0 && (
                        <div className="px-4 py-2 bg-slate-50/70 text-[10px] uppercase tracking-widest font-semibold text-slate-600">
                          Contracte care expiră
                        </div>
                      )}
                      {alertsItems.slice(0, 6).map((a) => (
                        <button
                          key={a.contract_id}
                          onClick={() => { setShowAlerts(false); nav(`/clienti/${a.client_id}`); }}
                          data-testid={`topbar-alert-${a.contract_id}`}
                          className="w-full text-left px-4 py-3 hover:bg-red-50 border-b border-border last:border-b-0 transition-colors"
                        >
                          <p className="text-sm font-medium truncate">{a.client_denumire}</p>
                          <p className="text-xs text-muted-foreground">Contract {a.numar}</p>
                          <p className={`text-xs mt-1 font-medium ${a.zile_ramase < 0 ? "text-red-700" : "text-orange-700"}`}>
                            {a.zile_ramase < 0
                              ? `Expirat de ${-a.zile_ramase} zile`
                              : a.zile_ramase === 0 ? "Expiră astăzi" : `Expiră în ${a.zile_ramase} zile`}
                          </p>
                        </button>
                      ))}
                      {pvAlertsItems.length > 0 && (
                        <div className="px-4 py-2 bg-slate-50/70 text-[10px] uppercase tracking-widest font-semibold text-slate-600">
                          PV-uri de făcut
                        </div>
                      )}
                      {pvAlertsItems.slice(0, 6).map((a) => (
                        <button
                          key={`pv-${a.client_id}`}
                          onClick={() => { setShowAlerts(false); nav(`/clienti/${a.client_id}`); }}
                          data-testid={`topbar-pv-alert-${a.client_id}`}
                          className="w-full text-left px-4 py-3 hover:bg-amber-50 border-b border-border last:border-b-0 transition-colors"
                        >
                          <p className="text-sm font-medium truncate">{a.client_denumire}</p>
                          <p className="text-xs text-muted-foreground">
                            Contract {a.contract_numar} · La {a.frecventa_luni} {a.frecventa_luni === 1 ? "lună" : "luni"}
                          </p>
                          <p className={`text-xs mt-1 font-medium ${a.zile_ramase < 0 ? "text-red-700" : "text-amber-700"}`}>
                            {a.zile_ramase < 0
                              ? `PV restant de ${-a.zile_ramase} zile`
                              : a.zile_ramase === 0 ? "PV azi" : `PV în ${a.zile_ramase} zile`}
                          </p>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Quick-add "+" menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className="h-10 px-4 gap-2 bg-gold text-charcoal hover:bg-gold-deep hover:text-white font-semibold shadow-md"
                data-testid="topbar-plus-btn"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Adaugă</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="label-eyebrow">Acțiune rapidă</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {hasPermission(user, "clients") && (
                <DropdownMenuItem
                  onClick={() => setNewClientOpen(true)}
                  data-testid="quick-add-client"
                  className="cursor-pointer"
                >
                  <Building2 className="w-4 h-4 mr-2 text-gold-deep" />
                  Client nou
                </DropdownMenuItem>
              )}
              {hasPermission(user, "clients") && (
                <DropdownMenuItem
                  onClick={() => setPickerAction("new-contract")}
                  data-testid="quick-add-contract"
                  className="cursor-pointer"
                >
                  <FileText className="w-4 h-4 mr-2 text-gold-deep" />
                  Contract nou
                </DropdownMenuItem>
              )}
              {hasPermission(user, "clients") && (
                <DropdownMenuItem
                  onClick={() => setPickerAction("new-pv")}
                  data-testid="quick-add-pv"
                  className="cursor-pointer"
                >
                  <ClipboardCheck className="w-4 h-4 mr-2 text-gold-deep" />
                  Proces Verbal nou
                </DropdownMenuItem>
              )}
              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => nav("/utilizatori?new=1")}
                    data-testid="quick-add-user"
                    className="cursor-pointer"
                  >
                    <UserPlus className="w-4 h-4 mr-2 text-gold-deep" />
                    Utilizator nou
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <NewClientDialog
        open={newClientOpen}
        onOpenChange={setNewClientOpen}
        onCreated={(c) => nav(`/clienti/${c.id}`)}
      />
      <PickClientDialog
        open={!!pickerAction}
        onOpenChange={(v) => !v && setPickerAction(null)}
        action={pickerAction || "new-contract"}
      />
    </>
  );
}

function SearchGroup({ label, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 px-4 py-2 bg-slate-50/70">
        <Icon className="w-3.5 h-3.5 text-gold-deep" />
        <p className="label-eyebrow">{label}</p>
      </div>
      <div>{children}</div>
    </div>
  );
}

function SearchItem({ title, subtitle, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="w-full text-left px-4 py-2.5 hover:bg-gold-soft/50 transition-colors block"
    >
      <p className="text-sm font-medium truncate">{title}</p>
      <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
    </button>
  );
}
